import React, { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Send } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Which project has the strongest booking momentum this quarter?",
  "Give me a cash-flow risk summary across all projects.",
  "Explain the JDA revenue-sharing exposure in the Riviera project.",
  "Which materials are below reorder level and what's the impact?",
  "Suggest three high-priority actions for our Sales head this week.",
];

export default function AIAssistant() {
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const [projectId, setProjectId] = useState("");
  const [messages, setMessages] = useState([
    { id: "m0", role: "assistant", text: "Namaste! I'm the Param Developers AI Business Assistant, powered by GPT 5.6 Terra. Ask about revenue, cash flow, JDA share, leads or forecasts — I have live ERP context." },
  ]);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  async function send(text) {
    const p = (text ?? prompt).trim();
    if (!p || busy) return;
    setMessages((m) => [...m, { id: `u_${Date.now()}`, role: "user", text: p }]);
    setPrompt(""); setBusy(true);
    try {
      const { data } = await api.post("/ai/assistant", { prompt: p, project_id: projectId || null, session_id: "aipage" });
      setMessages((m) => [...m, { id: `a_${Date.now()}`, role: "assistant", text: data.answer, ctx: data.context_used }]);
    } catch (e) {
      toast.error("AI unavailable");
      setMessages((m) => [...m, { id: `a_${Date.now()}`, role: "assistant", text: "AI service could not be reached." }]);
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-6" data-testid="ai-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">AI · Insights</div>
          <h1 className="font-heading text-3xl font-bold mt-1 flex items-center gap-2"><Sparkles className="text-emerald-700" size={26} /> Business Assistant</h1>
          <p className="text-sm text-zinc-500 mt-1">Ask anything about your projects — I have live context and answer in ₹ Cr / L.</p>
        </div>
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger className="w-72" data-testid="ai-project-context"><SelectValue placeholder="Ground my answer in a project (optional)" /></SelectTrigger>
          <SelectContent>
            <SelectItem value=" ">All projects (no context)</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="lg:col-span-3 bg-white border-zinc-200">
          <CardContent className="p-0 flex flex-col h-[65vh]">
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-zinc-50">
              {messages.map((m, i) => (
                <div key={m.id || `${m.role}-${i}`} className={`text-sm rounded-md px-4 py-3 max-w-[80%] whitespace-pre-wrap leading-relaxed ${
                  m.role === "user" ? "ml-auto bg-emerald-700 text-white" : "bg-white border border-zinc-200 text-zinc-800 shadow-sm"
                }`}>
                  {m.text}
                  {m.role === "assistant" && m.ctx && (
                    <div className="mt-1 text-[10px] uppercase tracking-widest text-emerald-700">✓ live project context used</div>
                  )}
                </div>
              ))}
              {busy && <div className="text-xs text-zinc-500 italic">Thinking…</div>}
            </div>
            <div className="p-4 border-t border-zinc-200 flex gap-2 bg-white">
              <input
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about cash flow, JDA share, forecast, materials, leads…"
                className="flex-1 border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                data-testid="ai-prompt"
              />
              <Button onClick={() => send()} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="ai-send"><Send size={14} /></Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Suggested prompts</div>
          {SUGGESTIONS.map((s, i) => (
            <button
              key={s}
              onClick={() => send(s)}
              className="w-full text-left text-sm p-3 border border-zinc-200 rounded-md bg-white hover:bg-zinc-50 hover:border-emerald-400 transition-colors"
              data-testid={`ai-suggestion-${i}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
